import { TextFieldTheme, TextField as TPATextField } from '@wix/wix-ui-tpa/cssVars';
import classNames from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useConfig } from '../../contexts/config-context';
import { useFocusFieldEvent } from '../../contexts/events-context';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
import { useFieldAttributes, useTranslation } from '../../sdk/components';
import { FieldDescriptionWUT, FieldErrorWUT, FieldLabelWUT } from '../../sdk/components-wut';
import { formatPrice, getCurrencyAriaLabel } from '../shared/payment-field-utils';
import { useInvokeCallback } from '../shared/use-invoke-callback';
import { toProductValue } from './payment-input-field-utils';
import { classes } from './payment-input-field-wut.st.css';
export const PaymentInputFieldWUT = ({ id, target, label, hideLabel = false, placeholder, required = false, value = null, onChange, onFocus, onBlur, error, hasError = false, errorMessage, disabled = false, readOnly, description, validation, FieldLayout, requiredIndicatorType, requiredIndicatorPlacement })=>{
    const { t } = useTranslation();
    const { labelId, inputId, errorId, descriptionId, ariaDescribedBy, prefixId } = useFieldAttributes(id, error, description);
    const { currency, regionalFormat } = useConfig();
    const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
    const invokeBlur = useInvokeCallback(onBlur);
    const inputRef = useRef(null);
    const paymentValidation = useMemo(()=>validation?.predefined?.paymentOptions?.products?.[0], [
        validation
    ]);
    const [paymentValue, setPaymentValue] = useState(String(value?.at(0)?.price ?? ''));
    const handleOnBlur = useCallback((event)=>{
        const price = formatPrice(event.target.value, regionalFormat);
        setPaymentValue(price ? price : '');
        invokeBlur();
    }, [
        regionalFormat,
        invokeBlur
    ]);
    const handleOnChange = useCallback((event)=>{
        setPaymentValue(event.target.value);
        const filteredInput = formatPrice(event.target.value, regionalFormat);
        if (filteredInput) {
            const normalizedValue = Number(filteredInput.replace(/,/g, '.'));
            onChange([
                toProductValue(paymentValidation, normalizedValue)
            ]);
        } else {
            onChange(null);
        }
    }, [
        setPaymentValue,
        onChange,
        paymentValidation,
        regionalFormat
    ]);
    useFocusFieldEvent(()=>{
        inputRef.current?.focus({
            preventScroll: true
        });
    }, id);
    return /*#__PURE__*/ React.createElement(FieldLayout, {
        fieldId: id,
        renderLabel: ()=>/*#__PURE__*/ React.createElement(FieldLabelWUT, {
                labelId: labelId,
                required: required,
                label: label,
                hideLabel: hideLabel,
                requiredIndicatorType: requiredIndicatorType,
                requiredIndicatorPlacement: requiredIndicatorPlacement
            }),
        renderInput: ()=>/*#__PURE__*/ React.createElement(TPATextField, {
                id: inputId,
                placeholder: placeholder,
                "aria-label": label,
                "aria-labelledby": `${labelId} ${prefixId}`,
                "aria-describedby": ariaDescribedBy,
                className: classNames(classes.paymentInput, classes.theme, {
                    [classes.hover]: forcedHover
                }),
                value: value ? paymentValue : '',
                name: target,
                onChange: handleOnChange,
                disabled: disabled,
                readOnly: readOnly,
                onBlur: handleOnBlur,
                onFocus: onFocus,
                theme: TextFieldTheme.Box,
                error: hasError,
                newErrorMessage: true,
                prefix: currency ? /*#__PURE__*/ React.createElement("span", {
                    id: prefixId,
                    "aria-hidden": false,
                    "aria-label": getCurrencyAriaLabel(t, currency)
                }, currency.sign || currency.code) : null,
                autoComplete: "off",
                required: required,
                ref: inputRef
            }),
        renderDescription: ()=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(FieldErrorWUT, {
                target: target,
                hasError: hasError,
                errorMessage: errorMessage,
                id: errorId
            }), /*#__PURE__*/ React.createElement(FieldDescriptionWUT, {
                description: description,
                id: descriptionId
            }))
    });
};

//# sourceMappingURL=payment-input-field-wut.js.map